/**
 * Contract validation utilities for AdUnitCreateRequest.
 *
 * Validates against the contract-defined schema (bafkal.yaml → AdUnitCreateRequest):
 *   required: [name, size, type, floorPrice]
 *   name:       string, minLength 1
 *   description: string | null (optional)
 *   size:       SemanticFormatKey enum
 *   type:       AdFamily enum
 *   status:     InventoryStatus enum (optional)
 *   floorPrice: number, minimum 0
 */

import semanticFormatKeys from '@bafkal/contracts-typescript/data/semantic-format-keys.json';
import adFamilies from '@bafkal/contracts-typescript/data/ad-families.json';
import inventoryStatuses from '@bafkal/contracts-typescript/data/inventory-statuses.json';

/**
 * Validates an AdUnitCreateRequest payload against contract constraints.
 * @param {object} data
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validateAdUnitCreateRequest = (data) => {
  const errors = [];

  if (!data.name || typeof data.name !== 'string' || data.name.trim().length === 0) {
    errors.push('Name is required and must be a non-empty string.');
  }

  if (!data.size || !semanticFormatKeys.includes(data.size)) {
    errors.push(`Size must be one of: ${semanticFormatKeys.join(', ')}.`);
  }

  if (!data.type || !adFamilies.includes(data.type)) {
    errors.push(`Type must be one of: ${adFamilies.join(', ')}.`);
  }

  if (data.floorPrice === '' || data.floorPrice === null || data.floorPrice === undefined) {
    errors.push('Floor price is required.');
  } else {
    const price = Number(data.floorPrice);
    if (isNaN(price) || price < 0) {
      errors.push('Floor price must be a number greater than or equal to 0.');
    }
  }

  if (data.status !== undefined && data.status !== null) {
    if (!inventoryStatuses.includes(data.status)) {
      errors.push(`Status must be one of: ${inventoryStatuses.join(', ')}.`);
    }
  }

  return { valid: errors.length === 0, errors };
};
